import { useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { MultiTabView } from "../../common/multitab/MultiTabView";
import { TabInfo } from "../../common/multitab/TabInfo";
import { STORE_ROUTE_PATH } from "../../navigation/Routes";
import { StorePayoutRulesTab } from "./payout-rules/StorePayoutRulesTab";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import { NavigationHelper } from "../../navigation/PrevLocationState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";
import { useUserContext } from "../../context/UserContext";
import { userIsAuthorized } from "../../rampart/RampartUtils";
import { StoreTundraAdminPage } from "./StoreTundraAdminPage";
import { StoreEditDetailsTab } from "./StoreEditDetailsTab";
import { StoreEditProvider, useStoreEditDetailsContext } from "./StoreEditDetailsContext";
import { StoreEditThemesTab } from "./StoreEditThemesTab";
import { ErrorIcon } from "evergreen-ui";
import { EmptyState } from "../../common/EmptyState";

type Params = { storeId: string; tabId?: StoreTabIds };

enum StoreTabIds {
    details = "details",
    domains = "domains",
    payoutRules = "payout-rules",
    themes = "themes",
    editInTaiga = "edit-in-taiga",
}

export const EDIT_IN_TAIGA_BASE_URL =
    "https://admin.shopify.com/store/junipersales/apps/taiga-theme-builder/themes/";

// This component is responsible for fetching the store data based on the query params
// and passing it to the loader component to fetch the storefront data
export const MultiTabEditStorePage = () => {
    const { storeId } = useParams<Params>();

    return (
        <StoreEditProvider storeId={storeId}>
            <MultiTabEditStorePageContent />
        </StoreEditProvider>
    );
};

export const MultiTabEditStorePageContent = () => {
    const history = useHistory();
    const { tabId, storeId } = useParams<Params>();
    const { user } = useUserContext();
    const { store, formValues, storeLoading, storeError } = useStoreEditDetailsContext();

    const showThemeRelatedTabs = useMemo(() => {
        return storeLoading || (!!store?.domain && store.domain !== "");
    }, [storeLoading, store]);

    const resolveSelectedTabIndex = useCallback(
        (tabId?: StoreTabIds): number => {
            switch (tabId) {
                // Themes tab is only visible if the store has a storefront
                // If no storefront data is available, ensure user is redirected to the details tab
                case StoreTabIds.themes:
                    return showThemeRelatedTabs ? 3 : 0;
                case StoreTabIds.payoutRules:
                    return 2;
                case StoreTabIds.domains:
                    return 1;
                case StoreTabIds.editInTaiga:
                case StoreTabIds.details:
                default:
                    return 0;
            }
        },
        [showThemeRelatedTabs]
    );

    const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);

    useEffect(() => {
        if (storeLoading) {
            return;
        }
        setSelectedTabIndex(resolveSelectedTabIndex(tabId));
    }, [storeLoading, resolveSelectedTabIndex, tabId]);

    const getTabs = useCallback(() => {
        const tabs: TabInfo[] = [
            {
                label: "Details",
                tabId: StoreTabIds.details,
                component: <StoreEditDetailsTab />,
            },
        ];

        // Add the admin tab if we have permissions (I've added two authorizations in case here.)
        if (userIsAuthorized(user, ["juniper.self-serve.admin"])) {
            tabs.push(
                {
                    label: "Domains",
                    tabId: StoreTabIds.domains,
                    component: (
                        <RampartAuthorized
                            requiredGroups={["juniper.self-serve.admin"]}
                            unauthorizedView={null}
                        >
                            <StoreTundraAdminPage />
                        </RampartAuthorized>
                    ),
                },
                {
                    label: "Payout Rules",
                    tabId: StoreTabIds.payoutRules,
                    component: (
                        <RampartAuthorized
                            requiredGroups={["juniper.self-serve.admin"]}
                            unauthorizedView={null}
                        >
                            <StorePayoutRulesTab storeId={storeId} />
                        </RampartAuthorized>
                    ),
                }
            );

            if (showThemeRelatedTabs) {
                tabs.push({
                    label: "Themes",
                    tabId: StoreTabIds.themes,
                    component: (
                        <StoreEditThemesTab />
                    ),
                });
            }

            if (showThemeRelatedTabs) {
                tabs.push({
                    label: "Edit in Taiga",
                    tabId: StoreTabIds.details,
                    redirect: EDIT_IN_TAIGA_BASE_URL + (store?.domain || ""),
                    openInNewTab: true,
                });
            }
        }

        return tabs;
    }, [store, storeId, user, showThemeRelatedTabs]);

    const tabs: TabInfo[] = getTabs();

    useEffect(() => {
        if (selectedTabIndex === null) {
            return;
        }

        NavigationHelper.pushPathname(
            history,
            generatePath(STORE_ROUTE_PATH, {
                storeId: storeId,
                tabId: tabs[selectedTabIndex].tabId,
            })
        );
    }, [history, selectedTabIndex, storeId, tabs]);

    if (storeLoading || selectedTabIndex === null) {
        return <CenteredSpinner />;
    }

    if (!storeLoading && storeError) {
        return (
            <EmptyState title={"Error loading store data"}>
                <ErrorIcon size={60} />
            </EmptyState>
        );
    }

    return store ? (
        <MultiTabView
            header={formValues.storeName.trim() || store.name}
            tabs={tabs}
            selectedTabIndex={selectedTabIndex}
            onTabSelected={setSelectedTabIndex}
            fullPathFormat={STORE_ROUTE_PATH}
            backButtonHeader="All Stores"
            backButtonOnClick={() => {
                history.push("/stores");
            }}
        />
    ) : (
        <CenteredSpinner />
    );
};
